"use client";

import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth0 } from "@auth0/auth0-react";
import "@/src/components/Home/LandingV6/landing-v6.css";
import {
  Nav,
  Button,
  StartWritingButton,
  QuarklePlanet,
} from "@/src/components/Home/LandingV6/ui";
import { Footer } from "@/src/components/Home/LandingV6/blocks";
import {
  Sparkle,
  Star,
  WaveRule,
  Ribbon,
  TapeSticker,
  CornerPin,
  OutlineDisplay,
  BaselineGrid,
  Asterism,
} from "@/src/components/Home/LandingV6/ornaments";
import { handlePayment, handleManageAccount } from "@/src/helpers/stripe";
import { useEditorContext } from "@/src/contexts/editorContext";
import { captureEvent } from "@/src/helpers/trackEvent";

function Check({ color = "var(--lime)" }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden style={{ color, marginTop: 3, flexShrink: 0 }}>
      <path d="M4 12 L10 18 L20 6" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlanCard({ plan, hi, cycle, onCta, loading, ctaLabel, error }) {
  return (
    <div
      style={{
        background: hi ? "var(--ink)" : "var(--paper-3)",
        color: hi ? "var(--paper)" : "var(--ink)",
        border: "1.5px solid var(--ink)",
        borderRadius: 14,
        padding: "32px 28px 26px",
        position: "relative",
        boxShadow: hi ? "10px 10px 0 var(--pink)" : "8px 8px 0 var(--ink)",
        transform: hi ? "translateY(-10px)" : "none",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {hi && (
        <div style={{ position: "absolute", top: -14, left: 26 }}>
          <Ribbon color="var(--lime)" rotate={-4} fg="var(--ink)">most writers pick this</Ribbon>
        </div>
      )}
      {/* ticket punch-holes */}
      <span aria-hidden style={{ position: "absolute", left: -8, top: "50%", width: 16, height: 16, borderRadius: 999, background: "var(--paper)", border: "1px solid var(--ink)", transform: "translateY(-50%)" }} />
      <span aria-hidden style={{ position: "absolute", right: -8, top: "50%", width: 16, height: 16, borderRadius: 999, background: "var(--paper)", border: "1px solid var(--ink)", transform: "translateY(-50%)" }} />
      <div className="caps" style={{ opacity: 0.7 }}>{plan.name}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
        <span className="tanker" style={{ fontSize: 64, lineHeight: 0.9 }}>{plan.price}</span>
        {plan.sub && <span className="mono" style={{ fontSize: 17, opacity: 1, fontWeight: 600 }}>{plan.sub}</span>}
        {plan.subLine && <span className="mono" style={{ fontSize: 11, opacity: 0.55, width: "100%" }}>{plan.subLine}</span>}
      </div>
      <p className="display" style={{ margin: "6px 0 18px", fontStyle: "italic", fontSize: 17, opacity: 0.85 }}>
        {plan.tag}
      </p>
      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 22px", display: "grid", gap: 10, flexGrow: 1 }}>
        {plan.feats.map((f, j) => (
          <li
            key={j}
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: 10,
              alignItems: "start",
              borderBottom: "1px dashed rgba(127,127,127,0.3)",
              paddingBottom: 10,
            }}
          >
            <Check color={hi ? "var(--lime)" : "var(--pink)"} />
            <span style={{ fontSize: 14 }}>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        variant={hi ? "lime" : "primary"}
        size="md"
        onClick={onCta}
        style={{ width: "100%", justifyContent: "center" }}
      >
        {loading ? "…" : error ? "try again" : ctaLabel}
      </Button>
      {plan.footnote && (
        <div style={{ marginTop: 10, textAlign: "center" }}>
          <span className="mono" style={{ fontSize: 10, opacity: 0.65, letterSpacing: "0.14em", textTransform: "uppercase" }}>
            {plan.footnote}
          </span>
        </div>
      )}
    </div>
  );
}

export default function PricingPage() {
  const [theme, setTheme] = useState("paper");
  const [cycle, setCycle] = useState("monthly");
  const [isProLoading, setProLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initiatePayment, setInitiatePayment] = useState(false);

  const router = useRouter();
  const { user, isAuthenticated, loginWithPopup, loginWithRedirect, getAccessTokenSilently, logout } = useAuth0();
  const { subscription } = useEditorContext();
  const isPro = subscription?.subscription_type === "pro";

  useEffect(() => {
    try {
      const s = window.localStorage.getItem("qv6-theme");
      if (s === "paper" || s === "midnight") setTheme(s);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !initiatePayment) return;
    if (isPro) {
      handleManageAccount(subscription, router, setError, setProLoading, getAccessTokenSilently, logout);
      setInitiatePayment(false);
    } else {
      const plan = cycle === "yearly" ? "pro-yearly" : "pro";
      handlePayment(plan, user, router, setError, setProLoading, getAccessTokenSilently, logout).then(() => {
        setInitiatePayment(false);
      });
    }
  }, [isAuthenticated, initiatePayment, isPro, cycle]);

  async function startProCheckout() {
    captureEvent("pricing_pro_clicked", { cycle });
    setError(null);
    if (!isAuthenticated) {
      try { await loginWithPopup(); } catch (e) { setError(e); return; }
    }
    setInitiatePayment(true);
  }

  const plans = [
    {
      name: "writer",
      price: "free",
      subLine: "no card, no time limit",
      tag: "everything you need to draft.",
      feats: [
        "1M tokens daily",
        "chat & the review room",
        "story atlas: build and maintain it yourself",
        "5 Drawing Room pictures a day · keep 10 per book",
        "version control: checkpoint and restore any draft",
        "milestones: streaks & daily progress",
        "unlimited books, unlimited drafts",
        "export to plain text & markdown",
      ],
      cta: isAuthenticated ? "you're all set" : "start writing free",
      onClick: () => {
        if (isAuthenticated) return;
        captureEvent("pricing_free_signup_clicked");
        loginWithRedirect({ authorizationParams: { screen_hint: "signup" } });
      },
      highlight: false,
    },
    {
      name: "quarkle pro",
      price: cycle === "monthly" ? "$4.99" : "$120",
      sub: cycle === "monthly" ? "first month · then $19.99" : "per year · save 50%",
      tag: "for the book you're finishing.",
      feats: [
        "self-evolving story atlas, kept in sync automatically",
        "200x the usage limits",
        "the best writing models, per scene",
        "12 specialist AI review passes",
        "50 Drawing Room pictures a day · no fixed gallery limit",
        "open expression: mature themes, violence, no content wall",
        "priority reading speed · priority support",
      ],
      cta: isPro ? "manage your subscription" : cycle === "monthly" ? "start pro · $4.99 first month" : "start pro · $120/yr",
      onClick: startProCheckout,
      highlight: true,
      footnote: "cancel in a click",
    },
    {
      name: "studio",
      price: "let's talk",
      subLine: "for rooms, teams & studios",
      tag: "co-writers, custom personas, shared memory.",
      feats: [
        "everything in pro",
        "shared book memory across seats",
        "custom editor personas per project",
        "early features · direct line to the team",
      ],
      cta: "email team@quarkle.ai",
      onClick: () => {
        captureEvent("pricing_studio_clicked");
        window.location.href = "mailto:team@quarkle.ai?subject=Quarkle%20Studio";
      },
      highlight: false,
    },
  ];

  return (
    <div className={`qv6 ${theme === "midnight" ? "theme-midnight" : ""}`}>
      <Nav />

      {/* Hero */}
      <section style={{ padding: "70px 40px 40px", position: "relative", overflow: "hidden" }}>
        <div aria-hidden style={{ position: "absolute", top: 40, right: -40, opacity: 0.1, pointerEvents: "none" }}>
          <OutlineDisplay text="pricing." size={300} color="var(--pink)" strokeW={1.8} />
        </div>
        <div aria-hidden style={{ position: "absolute", bottom: 40, left: 80 }}>
          <Asterism size={26} color="var(--clay)" />
        </div>
        <div aria-hidden style={{ position: "absolute", top: 80, right: 140 }}>
          <Sparkle size={36} color="var(--pink)" style={{ animation: "qv6-sparkle 3s infinite" }} />
        </div>
        <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", textAlign: "center" }}>
          <div style={{ display: "inline-flex", gap: 12, alignItems: "center", marginBottom: 14 }}>
            <WaveRule w={60} color="var(--ink)" />
            <span className="caps" style={{ color: "var(--pink)" }}>pricing · cozy &amp; fair</span>
            <WaveRule w={60} color="var(--ink)" />
          </div>
          <h1
            className="tanker"
            style={{
              fontSize: "clamp(60px, 8.2vw, 160px)",
              lineHeight: 0.88,
              letterSpacing: "-0.04em",
              margin: "0 0 18px",
            }}
          >
            pick a seat in <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>the room</span>.
          </h1>
          <p className="display" style={{ fontSize: 22, fontStyle: "italic", opacity: 0.85, maxWidth: 700, margin: "0 auto 26px", lineHeight: 1.5 }}>
            start writing free, no card needed. upgrade when you want twelve specialist AI passes, one clear consolidation, and an atlas that remembers your dog-character.
          </p>

          {/* Monthly / Yearly toggle */}
          <div
            style={{
              display: "inline-flex",
              gap: 4,
              padding: 4,
              background: "var(--paper-3)",
              border: "1px solid var(--ink)",
              borderRadius: 999,
              boxShadow: "3px 3px 0 var(--ink)",
            }}
          >
            {[
              { k: "monthly", label: "monthly" },
              { k: "yearly", label: "yearly · save 50%" },
            ].map((o) => {
              const on = cycle === o.k;
              return (
                <button
                  key={o.k}
                  onClick={() => setCycle(o.k)}
                  className="mono"
                  style={{
                    all: "unset",
                    cursor: "pointer",
                    padding: "8px 18px",
                    borderRadius: 999,
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.14em",
                    color: on ? "#fff" : "var(--ink)",
                    background: on ? "var(--pink)" : "transparent",
                    fontWeight: on ? 600 : 500,
                    transition: "all 0.2s ease",
                  }}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Plan cards */}
      <section style={{ padding: "40px 40px 100px", position: "relative" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 28, alignItems: "stretch" }}>
            {plans.map((p, i) => (
              <PlanCard
                key={i}
                plan={p}
                hi={p.highlight}
                cycle={cycle}
                onCta={p.onClick}
                loading={p.highlight && isProLoading}
                ctaLabel={p.cta}
                error={p.highlight && error}
              />
            ))}
          </div>
          <div style={{ marginTop: 36, textAlign: "center" }}>
            <span className="mono" style={{ fontSize: 11, opacity: 0.6 }}>
              all plans · your work is yours · export anytime · no training on your words, ever
            </span>
          </div>
          {error && (
            <div style={{ marginTop: 20, textAlign: "center" }}>
              <span className="mono" style={{ fontSize: 12, color: "var(--clay)" }}>
                something went wrong · please try again
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Comparison strip */}
      <section style={{ padding: "80px 40px", background: "var(--paper-3)", borderTop: "1.5px solid var(--ink)", borderBottom: "1.5px solid var(--ink)", position: "relative" }}>
        <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <BaselineGrid w={1600} h={700} cell={56} color="var(--ink)" opacity={0.06} />
        </div>
        <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative" }}>
          <CornerPin>what's actually different at pro</CornerPin>
          <h2 className="tanker" style={{ fontSize: "clamp(42px, 5.2vw, 84px)", margin: "14px 0 28px", lineHeight: 0.95 }}>
            the difference isn't{" "}
            <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>volume</span>.
            <br />
            it's <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>attention</span>.
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22 }}>
            <div style={{ background: "var(--paper-2)", border: "1.5px solid var(--ink)", borderRadius: 14, padding: "22px 26px", boxShadow: "6px 6px 0 var(--ink)" }}>
              <div className="caps" style={{ color: "var(--clay)", marginBottom: 10 }}>on the free plan</div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
                {[
                  "a taste of the full room",
                  "character, world, plot, rule memory (you build it)",
                  "5 Drawing Room pictures a day · keep 10 per book",
                  "inline edits · accept or keep mine",
                  "version control · milestones",
                  "1M tokens daily",
                  "export to plain text & markdown",
                ].map((t) => (
                  <li key={t} style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 10 }}>
                    <Check color="var(--clay)" />
                    <span style={{ fontSize: 15 }}>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ background: "var(--ink)", color: "var(--paper)", border: "1.5px solid var(--ink)", borderRadius: 14, padding: "22px 26px", boxShadow: "6px 6px 0 var(--pink)" }}>
              <div className="caps" style={{ color: "var(--pink)", marginBottom: 10 }}>on pro</div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
                {[
                  "12 specialist AI review passes + AI consolidation",
                  "unlimited books · self-evolving atlas",
                  "50 Drawing Room pictures a day · no fixed gallery limit",
                  "open expression: mature themes, no content wall",
                  "200x the usage limits",
                  "priority reading speed · priority support",
                ].map((t) => (
                  <li key={t} style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 10 }}>
                    <Check color="var(--lime)" />
                    <span style={{ fontSize: 15 }}>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Mini FAQ */}
      <section style={{ padding: "80px 40px", position: "relative" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <span className="caps" style={{ color: "var(--pink)" }}>the honest part</span>
          <h2 className="tanker" style={{ fontSize: "clamp(40px, 4.8vw, 72px)", margin: "10px 0 20px", lineHeight: 0.96 }}>
            questions before you <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>sign up</span>.
          </h2>
          <div style={{ borderTop: "1.5px solid var(--ink)" }}>
            {[
              { q: "can I cancel any time?", a: "yes. one click from your account page. you keep every draft you wrote." },
              { q: "do I need a card to start?", a: "no. the free writer plan runs without a card. you only add one when you choose pro." },
              { q: "is my writing used to train anything?", a: "no. never. we don't train on your book, don't sell the patterns, and can't read it for marketing." },
              { q: "what's the difference between free and pro?", a: "free gets you chat, the review room, a story atlas you maintain, and 5 Drawing Room pictures a day with up to 10 kept per book. pro gets you 50 pictures a day with no fixed gallery limit, plus 200x the text usage, the best writing models per scene, priority support, and a self-evolving atlas." },
              { q: "can I switch monthly ↔ yearly later?", a: "any time. we prorate the difference so nothing's wasted." },
            ].map((it, i) => (
              <details key={i} style={{ borderBottom: "1.5px solid var(--ink)", padding: "18px 0" }}>
                <summary style={{ cursor: "pointer", listStyle: "none", display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center" }}>
                  <span className="tanker" style={{ fontSize: "clamp(18px, 2vw, 26px)", letterSpacing: "-0.015em" }}>
                    {it.q}
                  </span>
                  <span className="mono" style={{ fontSize: 18, opacity: 0.5 }}>+</span>
                </summary>
                <p className="display" style={{ margin: "12px 0 0 0", fontSize: 18, fontStyle: "italic", color: "var(--pink)", maxWidth: 700 }}>
                  {it.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA strip */}
      <section style={{ padding: "80px 40px", background: "var(--ink)", color: "var(--paper)", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <BaselineGrid w={1600} h={500} cell={56} color="var(--paper)" opacity={0.08} />
        </div>
        <div style={{ position: "relative", maxWidth: 760, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <QuarklePlanet size={36} color="var(--pink)" ring="var(--paper-3)" />
            <span className="caps" style={{ color: "var(--lime)" }}>the room is waiting</span>
          </div>
          <h2 className="tanker" style={{ fontSize: "clamp(48px, 6.6vw, 110px)", margin: "0 0 18px", lineHeight: 0.9 }}>
            let's <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>start</span>.
          </h2>
          <div style={{ display: "inline-flex", gap: 14, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
            <StartWritingButton variant="pink" size="xl">start writing free →</StartWritingButton>
            <Button variant="subtle" size="lg" style={{ borderColor: "var(--paper)", color: "var(--paper)" }} onClick={() => { window.location.href = "/features"; }}>
              see what's included
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
